// Reddit Ads Pixel tracking utilities

declare global {
  interface Window {
    rdt: (action: string, ...args: any[]) => void;
  }
}

import { supabase } from '@/integrations/supabase/client';

export const REDDIT_PIXEL_ID = import.meta.env.VITE_REDDIT_PIXEL_ID;

// Generate unique tracking ID for deduplication
const generateTrackingId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create click ID for deduplication
const getClickId = (): string => {
  const existing = localStorage.getItem('reddit_click_id');
  if (existing) {
    return existing;
  }
  
  // Check URL for Reddit click ID parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlClickId = urlParams.get('rdt_cid');
  
  if (urlClickId) {
    localStorage.setItem('reddit_click_id', urlClickId);
    return urlClickId;
  }
  
  // Generate new click ID for deduplication
  const newClickId = generateTrackingId();
  localStorage.setItem('reddit_click_id', newClickId);
  return newClickId;
};

// Track server-side conversion event
const trackServerSideEvent = async (
  eventType: string, 
  customData?: Record<string, any>,
  userData?: Record<string, any>,
  testId?: string
) => {
  try {
    const clickId = getClickId();
    
    const { data, error } = await supabase.functions.invoke('reddit-conversions', {
      body: {
        eventType,
        customData,
        userData: {
          ipAddress: userData?.ipAddress,
          userAgent: navigator.userAgent,
          email: userData?.email,
          userId: userData?.userId,
          ...userData
        },
        clickId,
        testId
      }
    });

    if (error) {
      console.error('Reddit server-side tracking error:', error);
    } else {
      console.log('Reddit server-side tracking success:', data);
    }
  } catch (error) {
    console.error('Reddit server-side tracking failed:', error);
  }
};

export const trackRedditPageview = (path: string) => {
  if (!REDDIT_PIXEL_ID || typeof window.rdt !== 'function') {
    return;
  }

  // Send PageVisit event to Reddit Pixel
  window.rdt('track', 'PageVisit');

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('Reddit Pixel - Tracked PageVisit:', { path, REDDIT_PIXEL_ID });
  }
};

export const trackRedditEvent = async (
  eventType: string, 
  customData?: Record<string, any>,
  userData?: Record<string, any>,
  serverSide: boolean = true,
  testId?: string
) => {
  if (!REDDIT_PIXEL_ID || typeof window.rdt !== 'function') {
    return;
  }

  // Send client-side event to Reddit Pixel
  if (customData) {
    window.rdt('track', eventType, customData);
  } else {
    window.rdt('track', eventType);
  }

  // Send server-side event for deduplication and better tracking
  if (serverSide) {
    await trackServerSideEvent(eventType, customData, userData, testId);
  }

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('Reddit Pixel - Tracked event:', { 
      eventType, 
      customData, 
      serverSide,
      clickId: getClickId(),
      REDDIT_PIXEL_ID 
    });
  }
};

// Predefined event tracking functions with enhanced server-side tracking
export const trackRedditSignUp = async (userData?: Record<string, any>) => {
  await trackRedditEvent('SignUp', undefined, userData);
};

export const trackRedditLead = async (userData?: Record<string, any>) => {
  await trackRedditEvent('Lead', undefined, userData);
};

export const trackRedditPurchase = async (
  value?: number, 
  currency: string = 'USD',
  userData?: Record<string, any>
) => {
  const customData = value ? { value, currency } : undefined;
  await trackRedditEvent('Purchase', customData, userData);
};

export const trackRedditViewContent = async (
  contentType?: string,
  userData?: Record<string, any>,
  testId?: string
) => {
  const customData = contentType ? { content_type: contentType } : undefined;
  await trackRedditEvent('ViewContent', customData, userData, true, testId);
};

// Enhanced tracking for form submissions and key actions
export const trackRedditFormSubmit = async (
  formType: string,
  userData?: Record<string, any>
) => {
  await trackRedditEvent('Lead', { form_type: formType }, userData);
};

export const trackRedditDownload = async (
  downloadType: string,
  userData?: Record<string, any>
) => {
  await trackRedditEvent('ViewContent', { content_type: downloadType }, userData);
};